import {Component, MeshComponent, Object3D, PhysXComponent} from '@wonderlandengine/api';
import {Cursor} from '@wonderlandengine/components';
import {vec3, quat} from 'gl-matrix';
import {hapticFeedback} from '../utils/haptic-feedback.js';
import {CursorTarget} from '@wonderlandengine/components';
import {PhongMaterial} from '../types/materials.js';
import {CursorReticle, CursorType} from './cursor-reticle.js';
import {property} from '@wonderlandengine/api/decorators.js';

import {SoundManagerInstance, Sounds} from '../managers/soundmanager.js';

/**
 * Returns an euler angle (as degrees) representation of a quaternion
 * @param  {number []} out Euler angles, pitch-yaw-roll
 * @param  {any[] | Float32Array} quat Quaternion
 * @return {number[]} out
 */
export function getEuler(out: number[], quat: number[] | Float32Array): number[] {
    const x = quat[0],
        y = quat[1],
        z = quat[2],
        w = quat[3],
        x2 = x * x,
        y2 = y * y,
        z2 = z * z,
        w2 = w * w;
    const unit = x2 + y2 + z2 + w2;
    const test = x * w - y * z;
    if (test > 0.499995 * unit) {
        //TODO: Use glmatrix.EPSILON
        // singularity at the north pole
        out[0] = ((Math.PI / 2) * 180) / Math.PI;
        out[1] = (2 * Math.atan2(y, x) * 180) / Math.PI;
        out[2] = (0 * 180) / Math.PI;
    } else if (test < -0.499995 * unit) {
        //TODO: Use glmatrix.EPSILON
        // singularity at the south pole
        out[0] = ((-Math.PI / 2) * 180) / Math.PI;
        out[1] = (2 * Math.atan2(y, x) * 180) / Math.PI;
        out[2] = (0 * 180) / Math.PI;
    } else {
        out[0] = (Math.asin(2 * (x * z - w * y)) * 180) / Math.PI;
        out[1] = (Math.atan2(2 * (x * w + y * z), 1 - 2 * (z2 + w2)) * 180) / Math.PI;
        out[2] = (Math.atan2(2 * (x * y + z * w), 1 - 2 * (y2 + z2)) * 180) / Math.PI;
    }
    return out;
}

/**
 * door-interaction
 * Using Guide:
 * Add child collision object to the door's handle "WL_Door-Interaction"
 * Add to it "cursor-target" component and this component
 */
export class DoorInteraction extends Component {
    static TypeName = 'door-interaction';

    /* Cursor */
    @property.object()
    player?: Object3D;

    @property.string('cursorDoor')
    cursorStyle = 'cursorDoor';

    /* General */
    @property.bool(true)
    openOnce = true; /* Disable collision after opening door? */

    @property.float(2)
    speedSeconds = 2; /* Lerp Speed in Seconds. */
    @property.float(0)
    rotOffsetX = 0;
    @property.float(90)
    rotOffsetY = 90;
    @property.float(0)
    rotOffsetZ = 0;
    @property.float(0.12)
    hoverHighlightStrength = 0.12;

    private lerpTime = 100;
    private opening = false;
    private startRot: number[] = [];
    private currentRot?: number[] = [];
    private endRot: number[] = [];
    private lerpRatio?: number;
    private lerpResult?: vec3;

    start() {
        /* Add Cursor Functions */
        const target =
            this.object.getComponent(CursorTarget) ||
            this.object.addComponent(CursorTarget)!;

        target.onHover.add(this.onHover.bind(this));
        target.onUnhover.add(this.onUnHover.bind(this));
        target.onDown.add(this.onDown.bind(this));

        if (!this.object.parent) {
            console.warn('DoorInteraction component requires the parent to be set.');
            return;
        }

        /* Clone objectHover material, to only affect this object's material */
        this.object.parent.getComponents(MeshComponent).forEach((a) => {
            if (a.material) {
                a.material = a.material.clone();
            }
        });
        this.object.parent.children.forEach((c) => {
            const childMesh = c.getComponent(MeshComponent);
            if (childMesh && childMesh.material) {
                childMesh.material = childMesh.material.clone();
            }
        });

        /* Lerp Open Door */
        this.lerpTime = 100;
        this.opening = true; //Gets toggled
        this.startRot = getEuler([0, 0, 0], this.object.parent.getRotationLocal());
        this.currentRot = getEuler([0, 0, 0], this.object.parent.getRotationLocal());
        this.endRot = [
            this.startRot[0] + this.rotOffsetX,
            this.startRot[1] + this.rotOffsetY,
            this.startRot[2] + this.rotOffsetZ,
        ];
    }

    update(dt: number) {
        /* Lerp Open Door */
        this.lerpTime += dt;
        this.lerpRatio = this.lerpTime / this.speedSeconds; /* Seconds */
        if (this.lerpTime < this.speedSeconds) {
            if (this.opening) {
                /* Close Door */
                this.lerpResult = vec3.lerp(
                    vec3.create(),
                    this.currentRot as vec3,
                    this.startRot as vec3,
                    this.lerpRatio
                );
            } else {
                /* Open Door */
                this.lerpResult = vec3.lerp(
                    vec3.create(),
                    this.currentRot as vec3,
                    this.endRot as vec3,
                    this.lerpRatio
                );
            }
            const rotQuat = quat.fromEuler(
                quat.create(),
                this.lerpResult[0],
                this.lerpResult[1],
                this.lerpResult[2]
            ); /* Convert Rotation Euler to Quat */
            this.object.parent?.setRotationLocal(rotQuat);
        }
    }

    onHover(_: Object3D, cursor: Cursor) {
        if (!this.object.parent) {
            console.warn('DoorInteraction component requires the parent to be set.');
            return;
        }

        hapticFeedback(cursor.object, 0.5, 50);
        /* Highlight */
        this.object.parent.getComponents(MeshComponent).forEach((meshComponent) => {
            const material = meshComponent.material as PhongMaterial;
            material.emissiveColor = [
                this.hoverHighlightStrength,
                this.hoverHighlightStrength,
                this.hoverHighlightStrength,
                1,
            ];
        });
        this.object.parent.children.forEach((c) => {
            const meshComponent = c.getComponent(MeshComponent);
            if (meshComponent) {
                const material = meshComponent.material as PhongMaterial;
                material.emissiveColor = [
                    this.hoverHighlightStrength,
                    this.hoverHighlightStrength,
                    this.hoverHighlightStrength,
                    1,
                ];
            }
        });
        if (this.player) {
            const cursorReticle = this.player.getComponent(CursorReticle);
            if (cursorReticle) {
                /* Cursor */
                if (this.cursorStyle === 'cursorDoor') {
                    cursorReticle.changeCursorTexture(cursor, CursorType.Door);
                } else if (this.cursorStyle === 'cursorPointer') {
                    cursorReticle.changeCursorTexture(cursor, CursorType.Pointer);
                }
            }
        }
    }

    onUnHover(_: Object3D, cursor: Cursor) {
        if (!this.object.parent) {
            console.warn('DoorInteraction component requires the parent to be set.');
            return;
        }

        hapticFeedback(cursor.object, 0.3, 50);
        /* Highlight Reset to Default */
        this.object.parent.getComponents(MeshComponent).forEach((a) => {
            const material = a.material as PhongMaterial;
            if (material) {
                material.emissiveColor = [0, 0, 0, 1];
            }
        });
        this.object.parent.children.forEach((c) => {
            const meshComponent = c.getComponent(MeshComponent);
            if (meshComponent) {
                const material = meshComponent.material as PhongMaterial;
                material.emissiveColor = [0, 0, 0, 1];
            }
        });
        if (this.player) {
            const cursorReticle = this.player.getComponent(CursorReticle);
            if (cursorReticle) {
                /* Cursor */
                cursorReticle.changeCursorTexture(cursor, CursorType.Default);
            }
        }
    }

    onDown(_: Object3D, cursor: Cursor) {
        if (!this.object.parent) {
            console.warn('DoorInteraction component requires the parent to be set.');
            return;
        }

        hapticFeedback(cursor.object, 1.0, 20);
        /* Opening Door */

        if(this.opening) {
            SoundManagerInstance.playSound(Sounds.doorOpen);
        } else {
            /* Close after animation */
            setTimeout(() => {
                SoundManagerInstance.playSound(Sounds.doorClose);
            }, this.speedSeconds*1000);
        }

        this.opening = !this.opening; /* toggle */

        this.currentRot = getEuler([0, 0, 0], this.object.parent.getRotationLocal());
        this.lerpTime = 0; /* Start Lerp */
        if (this.openOnce) {
            const physxComponent = this.object.getComponent(PhysXComponent);
            if (physxComponent) {
                physxComponent.active = false; /* Disable collision after opening door */
            }
        }
    }
}
